import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { api } from '../../lib/api';
import {
  Clock, CheckCircle, ExternalLink, Globe, HardDrive,
  ArrowRight, Database, XCircle, BookOpen, Lock
} from 'lucide-react';

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
  domains: string[];
  plan: string;
  storage_used_bytes: number;
  created_at: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

const LOCKED_FEATURES_BY_PLAN: Record<string, { label: string; plan: string }[]> = {
  free: [
    { label: 'Blog', plan: 'Pro' },
    { label: 'Vertalingen', plan: 'Pro' },
    { label: 'Geavanceerde SEO', plan: 'Pro' },
    { label: 'Eigen domein', plan: 'Pro' },
    { label: 'Handmatige backups', plan: 'Pro' },
    { label: 'White label', plan: 'Business' },
  ],
  pro: [
    { label: 'White label', plan: 'Business' },
    { label: 'Prioriteit support', plan: 'Business' },
  ],
  business: [],
};

export default function TenantHome() {
  const { user } = useAuth();
  const { features, currentPlan, tenants: planTenants } = usePlan();
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [platformUser, setPlatformUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const me = await api.get<{ platformUser: any }>('/auth/me');
        setPlatformUser(me.platformUser);

        try {
          const data = await api.get<TenantInfo[]>('/tenants/mine');
          setTenants(data);
        } catch {
          // No tenants yet or endpoint not available
        }
      } catch { /* not authenticated */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const status = platformUser?.status || 'waitlist';
  const lockedFeatures = LOCKED_FEATURES_BY_PLAN[currentPlan] ?? [];

  // Storage usage calculation
  const totalStorageUsed = tenants.reduce((s, t) => s + (t.storage_used_bytes || 0), 0);
  const maxStorage = features?.max_storage_bytes ?? 500 * 1024 * 1024;
  const storagePercent = maxStorage > 0 ? Math.min(100, Math.round((totalStorageUsed / maxStorage) * 100)) : 0;

  // Sites usage
  const siteCount = tenants.length;
  const maxSites = features?.max_sites ?? 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welkom{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Beheer je Occiput websites</p>
      </div>

      {/* Status card */}
      <div className="card p-5">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
            status === 'approved' || status === 'active'
              ? 'bg-green-100 text-green-600'
              : status === 'rejected'
              ? 'bg-red-100 text-red-600'
              : 'bg-blue-100 text-blue-600'
          }`}>
            {status === 'approved' || status === 'active'
              ? <CheckCircle className="w-5 h-5" />
              : status === 'rejected'
              ? <XCircle className="w-5 h-5" />
              : <Clock className="w-5 h-5" />
            }
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              {status === 'waitlist' && 'Aanvraag in behandeling'}
              {status === 'approved' && 'Account goedgekeurd'}
              {status === 'active' && 'Account actief'}
              {status === 'rejected' && 'Aanvraag afgewezen'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {status === 'waitlist' && 'Je aanvraag wordt handmatig beoordeeld (1-3 werkdagen).'}
              {status === 'approved' && 'Je website wordt klaargemaakt.'}
              {status === 'active' && 'Je kunt je website(s) beheren.'}
              {status === 'rejected' && 'Neem contact op voor meer informatie.'}
            </p>
          </div>
        </div>
      </div>

      {/* Getting started / help card */}
      {tenants.length > 0 && (
        <div className="card p-5 bg-brand-50 border-brand-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-brand-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-gray-900">Eerste keer? Bekijk de handleiding</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                In het beheerpaneel van je site vind je onder "Handleiding" een overzicht van alle functies.
                Klik op <strong>Admin</strong> bij je site en ga naar Handleiding in het menu.
              </p>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                <span>1. Stel je kleuren en logo in bij Branding</span>
                <span>2. Upload foto's in het Home album voor de hero</span>
                <span>3. Maak pagina's met de pagina-bouwer</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sites overview */}
      {tenants.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Mijn websites</h2>
            <Link to="/dashboard/sites" className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
              Alles bekijken <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-2">
            {tenants.map(t => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-brand-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.domains?.[0] || t.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400">{formatBytes(t.storage_used_bytes || 0)}</p>
                    <p className="text-xs text-gray-400 capitalize">{t.plan}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>{t.status}</span>
                  {t.status === 'active' && t.domains?.[0] && (
                    <a href={`https://${t.domains[0]}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-gray-400 hover:text-brand-600 rounded transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan usage stats */}
      {tenants.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Websites</p>
                <p className="text-2xl font-bold text-gray-900">
                  {siteCount}{maxSites !== -1 && <span className="text-sm font-normal text-gray-400">/{maxSites}</span>}
                </p>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <HardDrive className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Opslag</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBytes(totalStorageUsed)}
                  <span className="text-sm font-normal text-gray-400">/{formatBytes(maxStorage)}</span>
                </p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${storagePercent > 90 ? 'bg-red-500' : storagePercent > 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${storagePercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Plan</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">
                  {currentPlan}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Locked features */}
      {tenants.length > 0 && lockedFeatures.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Beschikbaar na upgrade</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {lockedFeatures.map(f => (
              <div key={f.label} className="flex items-center gap-2 text-sm text-gray-500">
                <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span>{f.label}</span>
                <span className="ml-auto text-xs text-amber-600 font-medium">{f.plan}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-6">
              <div className="h-5 bg-gray-100 rounded animate-pulse w-48 mb-3" />
              <div className="h-4 bg-gray-100 rounded animate-pulse w-72" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
